import { Component, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalWizardComponent, ModalWizardPageComponent, ScApiService } from '@sc-ui';
import { texts } from './modal-start-project.texts';
import { isSalaryHidden } from 'src/services/hidden-salary';

@Component({
  standalone: false,
  selector: 'app-modal-start-project',
  templateUrl: './modal-start-project.component.html',
  styleUrls: ['./modal-start-project.component.scss']
})
export class ModalStartProjectComponent {

  @Input()
  language: 'de' | 'en' = 'de';

  texts = texts;

  isSalaryHidden = isSalaryHidden;

  tabs = [
    { id: 'details', text: texts.tabDetails, isEnabled: true },
    { id: 'jobsite', text: texts.tabJobsite, isEnabled: true },
    { id: 'comment', text: texts.tabComment, isEnabled: true }
  ];

  activeTab = 'type';

  // the type the user picked on the first page. It is only written to the project
  // once the start flow completed, so dismissing the modal leaves it untouched.
  selectedType: string;

  // a longlist is handed over without outreach, so it has no jobsite to review
  get isLonglist() {
    return this.selectedType == 'Longlist';
  }

  get visibleTabs() {
    return this.tabs.filter(t => t.id != 'jobsite' || !this.isLonglist);
  }

  project: any;

  comment: any;

  minSalaryInput = '';

  maxSalaryInput = '';

  @ViewChild('modal')
  modal: ModalWizardComponent;

  @ViewChild('typePage')
  typePage: ModalWizardPageComponent;

  @ViewChild('start')
  startPage: ModalWizardPageComponent;

  @ViewChild('jobsite')
  jobsitePage: ModalWizardPageComponent;

  @ViewChild('commentPage')
  commentPage: ModalWizardPageComponent;

  @ViewChild('confirmation')
  confirmationPage: ModalWizardPageComponent;

  isStarting = false;

  constructor(private api: ScApiService, private router: Router) { }

  show(project, activeTab = 'type') {
    this.project = project;
    this.selectedType = project?.Type;
    this.syncSalaryInputs();

    this.activeTab = activeTab;

    var startPage = this.typePage;

    if (activeTab == 'details') startPage = this.startPage;
    if (activeTab == 'jobsite') startPage = this.jobsitePage;
    if (activeTab == 'comment') startPage = this.commentPage;

    this.modal.show(startPage);
  }

  selectType(type: string) {
    this.selectedType = type;
  }

  showNextFromDetails() {
    if (this.isLonglist) this.showComment();
    else this.showJobsite();
  }

  showBackFromComment() {
    if (this.isLonglist) this.showDetails();
    else this.showJobsite();
  }

  selectTab(tab) {

    if (!tab.isEnabled || tab.id == this.activeTab) return;

    if (tab.id == 'details') this.showDetails();
    else if (tab.id == 'jobsite') this.showJobsite();
    else if (tab.id == 'comment') this.showComment();
  }

  showDetails() {
    this.activeTab = 'details';
    this.modal.navigateTo(this.startPage);
  }

  showType() {
    this.save();
    this.activeTab = 'type';
    this.modal.navigateTo(this.typePage);
  }

  showJobsite() {
    this.save();
    this.activeTab = 'jobsite';
    this.modal.navigateTo(this.jobsitePage);
  }

  showComment() {
    this.save();
    this.activeTab = 'comment';
    this.modal.navigateTo(this.commentPage);
  }

  showJobsiteReview() {
    this.save();
    this.modal.dismiss();
    this.router.navigateByUrl(`/projects/${this.project.Id}/jobsite-review`);
  }

  onMinSalaryChange(value: string) {
    this.minSalaryInput = this.normalizeSalaryInput(value);
    this.project.Profile.Compensation.MinSalary = this.parseSalary(this.minSalaryInput);
  }

  onMaxSalaryChange(value: string) {
    this.maxSalaryInput = this.normalizeSalaryInput(value);
    this.project.Profile.Compensation.MaxSalary = this.parseSalary(this.maxSalaryInput);
  }

  startProject() {

    if (this.isStarting) return;
    this.isStarting = true;

    var args = {
      Comment: this.comment,
      Type: this.selectedType
    };

    this.api.post('projects/' + this.project.Id + '/start', args).subscribe(() => {
      this.project.Status = 'AwaitingSourcing';
      this.project.Type = this.selectedType;
      this.modal.navigateTo(this.confirmationPage);
      this.isStarting = false;
    });
  }

  save() {
    this.project.Profile.Requirements.forEach(r => r.isSelected = false);
    this.api.put('projects', this.project).subscribe();
  }

  private syncSalaryInputs() {
    this.minSalaryInput = this.formatSalaryInput(this.project?.Profile?.Compensation?.MinSalary);
    this.maxSalaryInput = this.formatSalaryInput(this.project?.Profile?.Compensation?.MaxSalary);
  }

  private formatSalaryInput(value: number | string | null | undefined) {
    return value == null ? '' : String(value);
  }

  private normalizeSalaryInput(value: string | number | null | undefined) {
    return String(value ?? '').replace(/[^\d]/g, '');
  }

  private parseSalary(value: string) {
    return value ? Number(value) : null;
  }
}
